import { useEffect, useState } from "react";
import api, { formatRand, CATEGORIES, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Package, ShoppingCart, Users, Wallet, Plus, Pencil, Trash2 } from "lucide-react";

const STATUSES = ["Processing", "Packed", "Shipped", "Delivered", "Cancelled"];
const emptyForm = { name: "", category: "kids", type: "", price: "", images: "", sizes: "", colours: "", description: "", featured: false };

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      type: p.type,
      price: String(p.price),
      images: (p.images || []).join(", "),
      sizes: (p.sizes || []).join(", "),
      colours: (p.colours || []).map((c) => (c.image ? `${c.name} | ${c.image}` : c.name)).join("\n"),
      description: p.description || "",
      featured: p.featured || false,
      in_stock: p.in_stock ?? true,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        category: form.category,
        type: form.type,
        price: parseFloat(form.price) || 0,
        images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colours: form.colours
          .split("\n")
          .map((l) => l.split("|").map((x) => x.trim()))
          .filter(([name]) => name)
          .map(([name, image]) => ({ name, image: image || null })),
        description: form.description,
        featured: form.featured,
        in_stock: form.in_stock ?? true,
      };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const changeStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    toast.success("Order status updated");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
  };

  const statCards = [
    { icon: Package, label: "Products", value: stats?.products ?? "—" },
    { icon: ShoppingCart, label: "Orders", value: stats?.orders ?? "—" },
    { icon: Users, label: "Clients", value: stats?.clients ?? "—" },
    { icon: Wallet, label: "Revenue", value: stats ? formatRand(stats.revenue) : "—" },
  ];

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-purple-950 mb-2">Admin Dashboard</h1>
      <p className="text-muted-foreground mb-8">Manage your catalogue, inventory and orders.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-purple-100 p-5">
            <div className="w-10 h-10 rounded-xl brand-gradient flex items-center justify-center mb-3">
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-purple-950" data-testid={`stat-${s.label.toLowerCase()}`}>{s.value}</p>
            <p className="text-sm text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList className="mb-6">
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold text-lg">Inventory ({products.length})</h2>
            <button
              data-testid="admin-add-product-button"
              onClick={openAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90"
            >
              <Plus className="w-4 h-4" /> Add Product
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-purple-100 overflow-hidden overflow-x-auto">
            <Table data-testid="admin-inventory-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Colours</TableHead>
                  <TableHead>Sizes</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={mediaUrl(p.images?.[0])} alt="" className="w-10 h-12 rounded object-contain bg-purple-50 p-0.5" />
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-muted-foreground">{p.type}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{p.category}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[160px]">{(p.colours || []).map((c) => c.name).join(", ")}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[160px]">{(p.sizes || []).join(", ")}</TableCell>
                    <TableCell className="font-semibold text-sm">{formatRand(p.price)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-purple-50 text-purple-700">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`admin-delete-${p.id}`} onClick={() => del(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders">
          <h2 className="font-semibold text-lg mb-4">All Orders ({orders.length})</h2>
          {orders.length === 0 ? (
            <div className="bg-white rounded-2xl border border-purple-100 p-12 text-center text-muted-foreground">No orders yet.</div>
          ) : (
            <div className="space-y-4" data-testid="admin-orders-list">
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-2xl border border-purple-100 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-purple-950">{o.order_number}</p>
                      <p className="text-xs text-muted-foreground">{o.user_email} · {new Date(o.created_at).toLocaleDateString("en-ZA")}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold brand-gradient-text">{formatRand(o.total)}</span>
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger data-testid={`admin-order-status-${o.id}`} className="w-36 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-purple-50 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                    <p><strong className="text-gray-800">Ship to:</strong> {o.shipping?.full_name}, {o.shipping?.address}, {o.shipping?.city} {o.shipping?.postal_code}</p>
                    <p><strong className="text-gray-800">Items:</strong> {o.items.map((i) => `${i.name} (${[i.colour, i.size].filter(Boolean).join(", ")}) x${i.quantity}`).join("; ")}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-form-category" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Input data-testid="product-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="T-Shirt" className="mt-1 bg-white" />
              </div>
            </div>
            <div>
              <Label>Price (R)</Label>
              <Input data-testid="product-form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Image URLs (comma separated)</Label>
              <Textarea data-testid="product-form-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} placeholder="https://... , https://..." className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Sizes (comma separated)</Label>
              <Input data-testid="product-form-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="5Y, 6Y, 7Y" className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Colours (one per line: Colour name | optional image URL)</Label>
              <Textarea data-testid="product-form-colours" value={form.colours} onChange={(e) => setForm({ ...form, colours: e.target.value })} placeholder={"Brown & White | /api/static/products/hd/CKC-023-1.jpg\nPink & White"} className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="product-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="product-form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              Featured on homepage
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-full border border-purple-200 font-medium">Cancel</button>
            <button data-testid="product-form-save" onClick={save} disabled={saving} className="px-5 py-2 rounded-full brand-gradient text-white font-semibold disabled:opacity-50">
              {saving ? "Saving..." : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
